/**
 * Sube una imagen a Storage o pega una URL (fondo del lienzo y widget imagen).
 */
import React, { useEffect, useRef, useState } from 'react';
import { auth, storage } from '../../../firebaseConfig';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { useAuth } from '../../../AuthContext';
import { ink, studio } from '../studioUi';

export async function uploadCreatorImage(file, uid) {
    const ownerUid = auth.currentUser?.uid || uid;
    if (!file || !ownerUid) throw new Error('Falta el archivo o la sesión.');
    if (!String(file.type || '').startsWith('image/')) {
        throw new Error('Elige un archivo de imagen (PNG, JPG o WebP).');
    }
    const compressed = await imageCompression(file, {
        maxSizeMB: 1.2,
        maxWidthOrHeight: 2400,
        useWebWorker: true,
    });
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const contentType = (compressed.type && String(compressed.type).startsWith('image/'))
        ? compressed.type
        : (file.type || 'image/jpeg');
    const path = `creator-assets/${ownerUid}/${Date.now()}.${ext}`;
    const snapshot = await uploadBytes(storageRef(storage, path), compressed, { contentType });
    return getDownloadURL(snapshot.ref);
}

const ImageAssetField = ({ label = 'Imagen', value = '', onChange, compact = false }) => {
    const { user, withBusy } = useAuth();
    const [urlDraft, setUrlDraft] = useState(value || '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const fileRef = useRef(null);

    useEffect(() => {
        setUrlDraft(value || '');
    }, [value]);

    const applyUrl = (next) => {
        setUrlDraft(next);
        onChange(next);
    };

    const onFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setBusy(true);
        setError('');
        try {
            const url = await withBusy(
                () => uploadCreatorImage(file, user?.uid),
                'Subiendo imagen…'
            );
            applyUrl(url);
        } catch (err) {
            setError(err.message || 'No se pudo subir la imagen.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={ui.wrap}>
            {label ? <span style={ui.label}>{label}</span> : null}
            {value ? (
                <div style={ui.previewWrap}>
                    <img src={value} alt="" style={ui.preview} />
                    <button type="button" style={studio.ghostBtn} onClick={() => applyUrl('')}>Quitar</button>
                </div>
            ) : null}
            <div style={compact ? ui.row : ui.stack}>
                <button
                    type="button"
                    style={studio.ghostBtn}
                    disabled={busy || !user}
                    onClick={() => fileRef.current?.click()}
                >
                    {busy ? 'Subiendo…' : 'Subir archivo'}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={onFile}
                    style={{ display: 'none' }}
                />
                <input
                    type="url"
                    placeholder="O pega una URL de imagen"
                    value={urlDraft}
                    onChange={(e) => setUrlDraft(e.target.value)}
                    onBlur={() => onChange((urlDraft || '').trim())}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onChange((urlDraft || '').trim());
                        }
                    }}
                    style={studio.controlCompact}
                />
            </div>
            {error ? <div style={ui.error}>{error}</div> : null}
        </div>
    );
};

const ui = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 8 },
    label: { fontSize: '0.75rem', fontWeight: 600, color: ink.muted },
    row: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
    stack: { display: 'flex', flexDirection: 'column', gap: 8 },
    previewWrap: { display: 'flex', alignItems: 'center', gap: 8 },
    preview: {
        width: 64,
        height: 40,
        objectFit: 'cover',
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        backgroundColor: ink.wash,
    },
    error: { fontSize: '0.75rem', color: ink.danger, fontWeight: 600 },
};

export default ImageAssetField;
